import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { 
  PlaybackState, 
  TrackMetadata, 
  QueueTrack, 
  SubscriptionState,
  ConnectionState 
} from '../protocols/BATON';
import type { EntityEvent, EntityRecord } from './entities/entityTypes';
import type { EntityCollectionState } from './entities/entitySliceTypes';
import { reduceAlbumState, reduceArtistState, reducePersonState } from './entities/entityReducers';
import { diagnosticLog } from '../utils/diagnosticLogger';

// =============================================================================
// ZONE TYPES (for discovered audio renderers)
// =============================================================================

export interface Zone {
  id: string;
  name: string;
  type: 'upnp' | 'chromecast' | 'browser' | 'manual' | 'airplay' | 'connector' | 'local' | 'psaudio' | 'lua' | 'local-native';
  host: string;
  port: number;
  status: 'available' | 'connected' | 'playing' | 'offline';
  capabilities: {
    protocols: string[];
    streaming?: boolean;
    volume?: boolean;
    fixedVolume?: boolean;
  };
  lastSeen: number;
  metadata?: Record<string, unknown>;
}

// =============================================================================
// CLIENT DEVICE DETECTION
// =============================================================================

/**
 * Detects the type of client device running the UI.
 * This is separate from zoneName (which is the audio output destination).
 * 
 * Priority:
 * 1. iOS native app (webkit bridge present) = "iPhone"
 * 2. iPad user agent = "iPad"
 * 3. Mobile device = "Mobile"
 * 4. Desktop/other = "Desktop"
 */
function detectClientDevice(): string {
  // Check for Maestro (desktop app with native bridge)
  if (typeof window !== 'undefined' && (window as any).__MAESTRO_CONNECTOR__) {
    const platform = (window as any).__MAESTRO_CONNECTOR__.platform;
    if (platform === 'macos') return 'Mac';
    if (platform === 'windows') return 'Windows';
    return 'Connector';
  }

  // Check for iOS native app (Swift WebView with maestroNative bridge)
  if (typeof window !== 'undefined' && (window as any).__maestroNative?.platform === 'ios') {
    const ua = navigator.userAgent;
    if (/iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      return 'iPad';
    }
    return 'iPhone';
  }
  
  // Browser-based detection
  if (typeof navigator !== 'undefined') {
    const ua = navigator.userAgent;
    
    // iPad detection (including iPadOS 13+ which reports as Mac)
    if (/iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      return 'iPad';
    }
    
    // iPhone in browser
    if (/iPhone/.test(ua)) {
      return 'iPhone';
    }
    
    // Android tablet (typically larger screens)
    if (/Android/.test(ua) && !/Mobile/.test(ua)) {
      return 'Tablet';
    }
    
    // Android phone or other mobile
    if (/Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
      return 'Mobile';
    }
  }
  
  // Default to Desktop for desktop
  return 'Desktop';
}

// =============================================================================
// STATE INTERFACE
// =============================================================================

interface SystemState {
  zoneName: string;           // Audio output zone (where music plays)
  clientDeviceName: string;   // UI client device (where app is displayed)
  connectionState: ConnectionState;
  activeDevice: string | null;
}

interface DiscoveryState {
  discoveredZones: Zone[];      // All discovered audio renderers
  activeZone: Zone | null;      // Currently selected playback zone
  isScanning: boolean;          // Whether discovery is running
}

// =============================================================================
// MULTIZONE STATE (for grouped playback)
// =============================================================================

export interface GroupedZone {
  zoneId: string;
  zoneName: string;
  zoneType: string;
  relativeVolume: number;  // 0-100, percentage of master
  actualVolume: number;    // Calculated actual volume sent to renderer
  isMaster: boolean;
}

export interface MultizoneState {
  enabled: boolean;              // Multizone mode active
  masterZoneId: string | null;   // Which zone is master
  masterVolume: number;          // Current master volume level (0-100)
  groupedZones: GroupedZone[];   // All zones in the group with their relative volumes
}

interface MaestroState {
  // Playback
  playbackState: PlaybackState;
  currentTrack: TrackMetadata | null;
  position: number;
  duration: number;
  volume: number | null;
  muted: boolean;

  // Queue & Library
  queue: QueueTrack[];
  queueIndex: number;
  playlists: any[]; // Using any for now to match MVP UI structure
  
  // System
  system: SystemState;
  
  // Discovery (audio renderers)
  discovery: DiscoveryState;
  
  // Multizone (grouped playback)
  multizone: MultizoneState;
  
  // Connector bridge: active network renderer
  activeConnectorRenderer: { id: string; name: string } | null;
  
  // USB DAC: selected local USB DAC (macOS CoreAudio)
  selectedUsbDac: { id: string; name: string; uid?: string } | null;

  // Zone-switch handoff: cached stream URLs for instant renderer switching.
  // Primary URL + format for the currently-playing track.
  currentStreamUrl: string | null;
  currentStreamFormatId: number | null;
  // Alternate format pre-cached so switching between hi-res and CD is instant.
  alternateStreamUrl: string | null;
  alternateStreamFormatId: number | null;
  // True after playUri succeeds on the active renderer; reset on renderer change.
  rendererHasStream: boolean;

  // Transport layer: active source and queue repeat/shuffle
  activeSource: 'library' | 'radio';
  repeatMode: 'off' | 'one' | 'all';
  shuffled: boolean;
  shuffleOrder: number[];

  // When true, tapping a single track queues the rest of the album.
  // When false (default), tapping a single track plays only that track.
  continuousPlay: boolean;

  // Local audio suppression: true when WebAudioRenderer has intentionally paused
  // the <audio> element because a network renderer is the active zone.
  // Prevents the reconciler from blindly resuming in this case.
  localRendererSuppressed: boolean;
  localSuppressReason: string | null;

  // Exclusive audio engine error (shown to user, cleared on next play attempt)
  exclusiveError: string | null;

  // Desktop mode: 'standard' (browser-only) or 'full' (with Connector)
  desktopMode: 'standard' | 'full';

  // Tunnel status: true when the daemon has a live registered tunnel URL
  tunnelConnected: boolean;

  /** True on the Mac/PC that owns the scanned NAS library (local SQLite has albums). */
  isHomeLibraryHost: boolean;

  // Subscription
  subscription: SubscriptionState | null;

  // Entity pages (artist/person/album) - event-driven slices (single mutation point)
  entities: {
    artist: EntityCollectionState;
    person: EntityCollectionState;
    album: EntityCollectionState;
  };

  // Local library enrichment progress (persistent across navigation)
  enrichment: {
    active: boolean;
    queue: string[];       // album keys (folder paths) still pending
    currentAlbum: string | null;
    done: number;
    total: number;
  };
  
  // ACTIONS
  setPlaybackState: (state: PlaybackState) => void;
  setCurrentTrack: (track: TrackMetadata | null) => void;
  updateTime: (position: number, duration: number) => void;
  setVolume: (level: number | null) => void;
  setMuted: (muted: boolean) => void;
  setDuration: (duration: number) => void;
  
  setQueue: (queue: QueueTrack[], currentIndex: number) => void;
  setQueueIndex: (index: number) => void;
  addToQueue: (items: QueueTrack[]) => void;
  playNext: (items: QueueTrack[]) => void;
  removeFromQueue: (index: number) => void;
  reorderQueue: (fromIndex: number, toIndex: number) => void;
  clearUpcoming: () => void;
  jumpToQueueIndex: (index: number) => void;
  setPlaylists: (playlists: any[]) => void;
  
  setConnectionState: (state: ConnectionState) => void;
  setZoneName: (name: string) => void;
  setClientDeviceName: (name: string) => void;
  setActiveDevice: (deviceId: string | null) => void;
  
  // Discovery actions
  setDiscoveredZones: (zones: Zone[]) => void;
  setActiveZone: (zone: Zone | null) => void;
  setIsScanning: (scanning: boolean) => void;
  
  // Multizone actions
  setMultizoneState: (state: MultizoneState) => void;
  setMultizoneEnabled: (enabled: boolean) => void;
  setMultizoneMasterZone: (zoneId: string | null) => void;
  setMultizoneMasterVolume: (volume: number) => void;
  setMultizoneGroupedZones: (zones: GroupedZone[]) => void;
  updateGroupedZoneRelativeVolume: (zoneId: string, relativeVolume: number) => void;
  setGroupedZoneActualVolume: (zoneId: string, actualVolume: number) => void;

  // Connector bridge
  setActiveConnectorRenderer: (renderer: { id: string; name: string } | null) => void;
  setSelectedUsbDac: (dac: { id: string; name: string; uid?: string } | null) => void;
  setCurrentStreamUrl: (url: string | null) => void;
  setCurrentStreamFormatId: (id: number | null) => void;
  setAlternateStreamUrl: (url: string | null) => void;
  setAlternateStreamFormatId: (id: number | null) => void;
  setRendererHasStream: (has: boolean) => void;
  setExclusiveError: (error: string | null) => void;
  clearStreamCache: () => void;

  // Transport layer
  setActiveSource: (source: 'library' | 'radio') => void;
  setRepeatMode: (mode: 'off' | 'one' | 'all') => void;
  setShuffled: (shuffled: boolean, order?: number[]) => void;
  setContinuousPlay: (on: boolean) => void;

  // Session init — wipe stale renderer/zone state
  clearDiscoveredZones: () => void;
  clearConnectorState: () => void;

  // Local audio suppression
  setLocalRendererSuppressed: (suppressed: boolean, reason?: string | null) => void;

  // Desktop mode
  setDesktopMode: (mode: 'standard' | 'full') => void;

  // Tunnel
  setTunnelConnected: (connected: boolean) => void;
  setIsHomeLibraryHost: (host: boolean) => void;

  // Entity actions
  dispatchEntityEvent: (event: EntityEvent) => void;

  // Enrichment actions
  triggerEnrichment: () => void;
  setEnrichmentQueue: (queue: string[], total: number) => void;
  advanceEnrichment: (albumName: string) => void;
  finishEnrichment: () => void;

}

// =============================================================================
// LAST-PLAYED PERSISTENCE (localStorage)
// =============================================================================

const LAST_PLAYED_KEY = 'maestro_last_played';

function stripStreamData(track: any): any {
  if (!track) return track;
  const { streamUrl, formatId, format, bitDepth, sampleRate, ...rest } = track;
  return rest;
}

function loadLastPlayed(): {
  currentTrack: TrackMetadata | null;
  queue: QueueTrack[];
  queueIndex: number;
  position: number;
  duration: number;
} {
  try {
    const raw = localStorage.getItem(LAST_PLAYED_KEY);
    if (!raw) return { currentTrack: null, queue: [], queueIndex: 0, position: 0, duration: 0 };
    const data = JSON.parse(raw);
    const restoredTrack = data.currentTrack ? stripStreamData(data.currentTrack) : null;
    const restoredQueue = Array.isArray(data.queue)
      ? data.queue.map((q: any) => q?.track ? { ...q, track: stripStreamData(q.track) } : q)
      : [];
    return {
      currentTrack: restoredTrack,
      queue: restoredQueue,
      queueIndex: typeof data.queueIndex === 'number' ? data.queueIndex : 0,
      position: typeof data.position === 'number' ? data.position : 0,
      duration: typeof data.duration === 'number' ? data.duration : 0,
    };
  } catch {
    return { currentTrack: null, queue: [], queueIndex: 0, position: 0, duration: 0 };
  }
}

const _restored = loadLastPlayed();

// =============================================================================
// ZUSTAND STORE
// =============================================================================

export const useMaestroStore = create<MaestroState>()(
  devtools(
    (set, get) => ({
      // Initial State — restore last played track so the playbar is never empty
      playbackState: _restored.currentTrack ? 'paused' : 'idle',
      currentTrack: _restored.currentTrack,
      position: _restored.position,
      duration: _restored.duration,
      volume: null,
      muted: false,
      queue: _restored.queue,
      queueIndex: _restored.queueIndex,
      playlists: [],
      system: {
        // Default output zone:
        // - iOS native shell: iPhone is the controller/device output label
        // - Web/Desktop: default to local browser playback (System Audio)
        zoneName: (typeof window !== 'undefined' && (window as any).__maestroNative?.platform === 'ios')
          ? 'iPhone'
          : 'System Audio',
        clientDeviceName: detectClientDevice(), // Auto-detect UI client device
        connectionState: "disconnected",
        activeDevice: null
      },
      discovery: {
        discoveredZones: [],
        activeZone: null,
        isScanning: false,
      },
      multizone: {
        enabled: false,
        masterZoneId: null,
        masterVolume: 50,
        groupedZones: [],
      },
      activeConnectorRenderer: null,
      selectedUsbDac: null,
      currentStreamUrl: null,
      currentStreamFormatId: null,
      alternateStreamUrl: null,
      alternateStreamFormatId: null,
      rendererHasStream: false,
      activeSource: 'library' as const,
      repeatMode: 'off' as const,
      shuffled: false,
      shuffleOrder: [],
      continuousPlay: (() => {
        try {
          const v = localStorage.getItem('maestro_continuous_play');
          return v === null ? true : v === '1';
        } catch { return true; }
      })(),
      desktopMode: (() => {
        // Activate 'full' automatically if the Maestro bridge is detected
        if (typeof window !== 'undefined' && (window as any).__MAESTRO_CONNECTOR__) {
          return 'full' as const;
        }
        try { localStorage.removeItem('maestro_desktop_mode'); } catch {}
        return 'standard' as const;
      })(),
      exclusiveError: null,
      localRendererSuppressed: false,
      localSuppressReason: null,
      tunnelConnected: false,
      isHomeLibraryHost: false,
      subscription: null,

      entities: {
        artist: { entityType: 'artist', currentKey: null, byKey: {} },
        person: { entityType: 'person', currentKey: null, byKey: {} },
        album: { entityType: 'album', currentKey: null, byKey: {} },
      },

      enrichment: {
        active: false,
        queue: [],
        currentAlbum: null,
        done: 0,
        total: 0,
      },

      // Actions
      setPlaybackState: (state) => set({ playbackState: state }),
      setCurrentTrack: (track) => set({ currentTrack: track }),
      updateTime: (position, duration) => set({ position, duration }),
      setVolume: (level) => set({ volume: level }),
      setMuted: (muted) => set({ muted }),
      setDuration: (duration) => set({ duration }),
      
      setQueue: (queue, currentIndex) => set({ queue, queueIndex: currentIndex }),
      setQueueIndex: (index) => set({ queueIndex: index }),
      addToQueue: (items) => set((state) => ({ queue: [...state.queue, ...items] })),
      playNext: (items) => set((state) => {
        const newQueue = [...state.queue];
        newQueue.splice(state.queueIndex + 1, 0, ...items);
        return { queue: newQueue, shuffled: false, shuffleOrder: [] };
      }),
      removeFromQueue: (index) => set((state) => {
        const newQueue = state.queue.filter((_, i) => i !== index);
        let newIndex = state.queueIndex;
        if (index < state.queueIndex) newIndex = Math.max(0, newIndex - 1);
        else if (index === state.queueIndex && newIndex >= newQueue.length) newIndex = Math.max(0, newQueue.length - 1);
        return { queue: newQueue, queueIndex: newIndex };
      }),
      reorderQueue: (fromIndex, toIndex) => set((state) => {
        if (fromIndex === toIndex) return state;
        const newQueue = [...state.queue];
        const [moved] = newQueue.splice(fromIndex, 1);
        newQueue.splice(toIndex, 0, moved);
        let newIdx = state.queueIndex;
        if (fromIndex === newIdx) {
          newIdx = toIndex;
        } else {
          if (fromIndex < newIdx && toIndex >= newIdx) newIdx--;
          else if (fromIndex > newIdx && toIndex <= newIdx) newIdx++;
        }
        return { queue: newQueue, queueIndex: newIdx };
      }),
      clearUpcoming: () => set((state) => ({
        queue: state.queue.slice(0, state.queueIndex + 1),
      })),
      jumpToQueueIndex: (index) => set({ queueIndex: index }),
      setPlaylists: (playlists) => set({ playlists }),
      
      setConnectionState: (s) => set((state) => ({ system: { ...state.system, connectionState: s } })),
      setZoneName: (name) => set((state) => ({ system: { ...state.system, zoneName: name } })),
      setClientDeviceName: (name) => set((state) => ({ system: { ...state.system, clientDeviceName: name } })),
      setActiveDevice: (id) => set((state) => ({ system: { ...state.system, activeDevice: id } })),
      
      // Discovery actions
      setDiscoveredZones: (zones) => set((state) => ({ discovery: { ...state.discovery, discoveredZones: zones } })),
      setActiveZone: (zone) => set((state) => ({ 
        discovery: { ...state.discovery, activeZone: zone },
        // Also update zoneName for compatibility
        system: { ...state.system, zoneName: zone?.name || 'System Audio' }
      })),
      setIsScanning: (scanning) => set((state) => ({ discovery: { ...state.discovery, isScanning: scanning } })),
      
      // Multizone actions
      setMultizoneState: (multizoneState) => set({ multizone: multizoneState }),
      setMultizoneEnabled: (enabled) => set((state) => ({ 
        multizone: { ...state.multizone, enabled } 
      })),
      setMultizoneMasterZone: (zoneId) => set((state) => ({ 
        multizone: { ...state.multizone, masterZoneId: zoneId } 
      })),
      setMultizoneMasterVolume: (volume) => set((state) => ({ 
        multizone: { ...state.multizone, masterVolume: volume } 
      })),
      setMultizoneGroupedZones: (zones) => set((state) => ({ 
        multizone: { ...state.multizone, groupedZones: zones } 
      })),
      updateGroupedZoneRelativeVolume: (zoneId, relativeVolume) => set((state) => ({
        multizone: {
          ...state.multizone,
          groupedZones: state.multizone.groupedZones.map(z => 
            z.zoneId === zoneId 
              ? { ...z, relativeVolume, actualVolume: Math.round(state.multizone.masterVolume * (relativeVolume / 100)) }
              : z
          ),
        }
      })),
      setGroupedZoneActualVolume: (zoneId, actualVolume) => set((state) => {
        const updatedZones = state.multizone.groupedZones.map(z =>
          z.zoneId === zoneId ? { ...z, actualVolume: Math.max(0, Math.min(100, actualVolume)) } : z
        );
        const avg = updatedZones.length > 0
          ? Math.round(updatedZones.reduce((sum, z) => sum + z.actualVolume, 0) / updatedZones.length)
          : 50;
        return {
          multizone: { ...state.multizone, masterVolume: avg, groupedZones: updatedZones },
        };
      }),

      setActiveConnectorRenderer: (renderer) => {
        const current = get().activeConnectorRenderer;
        const idChanging = current?.id !== renderer?.id;
        const z = get().discovery?.discoveredZones?.find((dz: Zone) => dz.id === renderer?.id);
        diagnosticLog(
          'info',
          'transport',
          `Active renderer set: ${renderer?.name || 'local'}`,
          {
            renderer: renderer?.name,
            type: renderer ? (z?.type || 'unknown') : 'local',
            ip: z?.host || undefined,
          },
        );
        set({
          activeConnectorRenderer: renderer,
          ...(idChanging ? { rendererHasStream: false } : {}),
          ...(renderer ? { selectedUsbDac: null } : {}),
        });
      },
      setSelectedUsbDac: (dac) => set({ selectedUsbDac: dac }),
      setCurrentStreamUrl: (url) => set({ currentStreamUrl: url }),
      setCurrentStreamFormatId: (id) => set({ currentStreamFormatId: id }),
      setAlternateStreamUrl: (url) => set({ alternateStreamUrl: url }),
      setAlternateStreamFormatId: (id) => set({ alternateStreamFormatId: id }),
      setRendererHasStream: (has) => set({ rendererHasStream: has }),
      setExclusiveError: (error) => set({ exclusiveError: error }),
      clearStreamCache: () => {
        const state = get();
        const isLocal = state.currentTrack?.source === 'local_library'
          || (state.currentTrack?.file_path && !state.currentTrack?.qobuzTrackId);
        if (isLocal && state.currentStreamUrl) {
          console.log('[Store] clearStreamCache — preserving local file URL (non-expiring)');
          set({ alternateStreamUrl: null, alternateStreamFormatId: null });
        } else {
          set({
            currentStreamUrl: null, currentStreamFormatId: null,
            alternateStreamUrl: null, alternateStreamFormatId: null,
          });
        }
      },

      setActiveSource: (source) => set({ activeSource: source }),
      setRepeatMode: (mode) => set({ repeatMode: mode }),
      setShuffled: (shuffled, order) => set({ shuffled, shuffleOrder: order || [] }),
      setContinuousPlay: (on) => {
        set({ continuousPlay: on });
        try { localStorage.setItem('maestro_continuous_play', on ? '1' : '0'); } catch {}
      },

      clearDiscoveredZones: () => set((state) => ({
        discovery: { ...state.discovery, discoveredZones: [], activeZone: null, isScanning: false },
      })),
      clearConnectorState: () => set({
        activeConnectorRenderer: null,
        rendererHasStream: false,
      }),

      setLocalRendererSuppressed: (suppressed, reason = null) => set({
        localRendererSuppressed: suppressed,
        localSuppressReason: suppressed ? (reason ?? null) : null,
      }),

      setDesktopMode: (mode) => {
        set({ desktopMode: mode });
      },

      setTunnelConnected: (connected) => set({ tunnelConnected: connected }),
      setIsHomeLibraryHost: (host) => set({ isHomeLibraryHost: host }),

      dispatchEntityEvent: (event) =>
        set((state) => {
          const nextArtist = reduceArtistState(state.entities.artist, event);
          const nextPerson = reducePersonState(state.entities.person, event);
          const nextAlbum = reduceAlbumState(state.entities.album, event);
          return {
            entities: {
              artist: nextArtist,
              person: nextPerson,
              album: nextAlbum,
            },
          };
        }),

      triggerEnrichment: () => set((state) => ({
        enrichment: state.enrichment.active
          ? state.enrichment
          : { ...state.enrichment, active: true, done: 0, total: 0, queue: [], currentAlbum: null },
      })),
      setEnrichmentQueue: (queue, total) => set((state) => ({
        enrichment: { ...state.enrichment, queue, total },
      })),
      advanceEnrichment: (albumName) => set((state) => ({
        enrichment: {
          ...state.enrichment,
          currentAlbum: albumName,
          done: state.enrichment.done + 1,
          queue: state.enrichment.queue.slice(1),
        },
      })),
      finishEnrichment: () => set({
        enrichment: { active: false, queue: [], currentAlbum: null, done: 0, total: 0 },
      }),

    }),
    { name: 'MaestroStore' }
  )
);

// Persist playback state to localStorage so the playbar survives page reloads
// and offline cold-starts. Position guard (>=10s delta) prevents writing on
// every tick. QueueSession DO is the cross-device authoritative source;
// localStorage is the instant-load cache only.
useMaestroStore.subscribe((state, prev) => {
  if (
    state.currentTrack === prev.currentTrack &&
    state.queue === prev.queue &&
    state.queueIndex === prev.queueIndex &&
    Math.abs(state.position - prev.position) < 10 &&
    state.duration === prev.duration
  ) return;
  try {
    localStorage.setItem(LAST_PLAYED_KEY, JSON.stringify({
      currentTrack: state.currentTrack,
      queue: state.queue,
      queueIndex: state.queueIndex,
      position: state.position,
      duration: state.duration,
    }));
  } catch {}
});

// =============================================================================
// ATOMIC SELECTORS (Prevents Infinite Loops)
// =============================================================================

export const usePlaybackState = () => useMaestroStore((s) => s.playbackState);
export const useCurrentTrack = () => useMaestroStore((s) => s.currentTrack);
export const useVolume = () => useMaestroStore((s) => ({ volume: s.volume, muted: s.muted })); // Careful: returns new object
export const useQueue = () => useMaestroStore((s) => ({ tracks: s.queue, currentIndex: s.queueIndex })); // Careful
export const usePlaylists = () => useMaestroStore((s) => s.playlists);

// System selectors
export const useSystem = () => useMaestroStore((s) => s.system);

// Computed selectors
export const useIsPlaying = () => useMaestroStore((s) => s.playbackState === 'playing');
export const useNextTrack = () => useMaestroStore((s) => {
  if (s.queue.length === 0 || s.queueIndex >= s.queue.length - 1) return null;
  return s.queue[s.queueIndex + 1].track;
});

// Time selector (Updated frequently, keep atomic)
export const usePlaybackTime = () => useMaestroStore((s) => ({ position: s.position, duration: s.duration }));

// Discovery selectors
export const useDiscovery = () => useMaestroStore((s) => s.discovery);
export const useDiscoveredZones = () => useMaestroStore((s) => s.discovery.discoveredZones);
export const useActiveZone = () => useMaestroStore((s) => s.discovery.activeZone);

// Desktop mode selectors
export const useDesktopMode = () => useMaestroStore((s) => s.desktopMode);

// Tunnel status selector
export const useTunnelConnected = () => useMaestroStore((s) => s.tunnelConnected);

// Multizone selectors
export const useMultizone = () => useMaestroStore((s) => s.multizone);
export const useMultizoneEnabled = () => useMaestroStore((s) => s.multizone.enabled);
export const useMultizoneMasterZoneId = () => useMaestroStore((s) => s.multizone.masterZoneId);
export const useMultizoneMasterVolume = () => useMaestroStore((s) => s.multizone.masterVolume);
export const useMultizoneGroupedZones = () => useMaestroStore((s) => s.multizone.groupedZones);

// =============================================================================
// ENTITY SELECTORS
// =============================================================================

export const useSelectedArtistRecord = () =>
  useMaestroStore((s) => (s.entities.artist.currentKey ? s.entities.artist.byKey[s.entities.artist.currentKey] : null) as EntityRecord | null);

export const useSelectedPersonRecord = () =>
  useMaestroStore((s) => (s.entities.person.currentKey ? s.entities.person.byKey[s.entities.person.currentKey] : null) as EntityRecord | null);

export const useSelectedAlbumRecord = () =>
  useMaestroStore((s) => (s.entities.album.currentKey ? s.entities.album.byKey[s.entities.album.currentKey] : null) as EntityRecord | null);
